/**
 * A definition's actions: Edit, Open in GitLab, Withdraw.
 *
 * - Edit (with `can_edit`, once the page can open the editor) starts from
 *   the text Save will replace: Sanctum's MR's (`mr_content`) when
 *   `sanctum_mr` is set, else the default branch's (`content`). It reads
 *   "Edit (pushes to !N)" when Save would push to Sanctum's open MR. It
 *   waits (disabled) while the detail refetches, so an edit never starts
 *   from cached text a refetch is replacing (a Save refused as stale
 *   refetches it). It is disabled, with the reason shown, while the
 *   project's setup is incomplete (`editBlocked`); Withdraw never is.
 * - Withdraw (with `can_withdraw`) closes Sanctum's own `sanctum-def/<name>`
 *   MR, which may not be the newest MR shown, and deletes its branch. It
 *   asks inline first, never with `window.confirm`, and sends the !N it
 *   asked about, so the backend refuses (409) rather than close another
 *   MR opened since. The outcome, a refusal included, goes to
 *   `onWithdrawOutcome` (null on the next confirm clears it): the page keeps
 *   it, since a refetch replaces or drops this Withdraw (a 409 refetches,
 *   and the detail then names a newer MR or none) and a withdrawn proposed
 *   definition leaves the list, this pane with it. It is taken from the
 *   request's own promise, which settles even after Withdraw unmounts.
 * - The MR it closed is `onClosed`'s, held by `DefinitionDetail` so it
 *   outlives a tab switch: Withdraw stays hidden for `closedIid` while the
 *   detail still names it, and comes back for a newer Sanctum MR.
 */

import { useEffect, useRef, useState, type ReactElement } from 'react';
import { useWithdrawDefinition } from '../hooks/useProjectPanelMutations';
import { panelErrorMessage } from '../utils/errors';
import type { DefinitionDetail, WithdrawResponse } from '../types/projectPanel.types';

/** What the editor opens with: the name and the text it starts from. */
export interface DefinitionEditStart {
  name: string;
  content: string;
}

/** A withdraw's outcome for the page: what it closed, or why it didn't. */
export interface WithdrawOutcome {
  message: string;
  /** Refused (a 409) or failed: nothing was closed. */
  failed: boolean;
}

interface DefinitionActionsProps {
  projectId: string;
  detail: DefinitionDetail;
  /** The MR, or the file on the default branch; null when unknown. */
  openUrl: string | null;
  onEdit?: (start: DefinitionEditStart) => void;
  /** The detail is refetching: Edit waits for the newer text. */
  refreshing?: boolean;
  /** Why Edit is unavailable (setup incomplete); null when it isn't. */
  editBlocked?: string | null;
  /** The MR this pane last closed, kept by `DefinitionDetail`. */
  closedIid: number | null;
  onClosed: (iid: number) => void;
  /** The outcome for the page to show; null clears it. */
  onWithdrawOutcome: (outcome: WithdrawOutcome | null) => void;
}

function editStart(detail: DefinitionDetail): DefinitionEditStart | null {
  const content = detail.sanctum_mr !== null ? detail.mr_content : detail.content;
  return content === null ? null : { name: detail.summary.name, content };
}

/** The outcome, from the MR the backend says it closed. */
function withdrawn(result: WithdrawResponse): string {
  const tag = `!${String(result.mr_iid)}`;
  if (result.branch_deleted) return `Withdrew ${tag} and deleted its branch.`;
  return `Withdrew ${tag}; ${result.reason ?? 'the branch was left in place'}`;
}

interface WithdrawProps {
  projectId: string;
  name: string;
  iid: number;
  onDone: (result: WithdrawResponse) => void;
  onOutcome: (outcome: WithdrawOutcome | null) => void;
}

function Withdraw({ projectId, name, iid, onDone, onOutcome }: WithdrawProps): ReactElement {
  const [confirming, setConfirming] = useState(false);
  const withdraw = useWithdrawDefinition(projectId);
  // Each trigger unmounts as the other mounts: focus follows, so it never falls to <body>.
  const asked = useRef(false);
  const ask = useRef<HTMLButtonElement>(null);
  const confirm = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (confirming) confirm.current?.focus();
    else if (asked.current) ask.current?.focus();
  }, [confirming]);
  const toggle = (next: boolean): void => {
    asked.current = true;
    setConfirming(next);
  };
  const tag = `!${String(iid)}`;
  const close = (): void => {
    onOutcome(null);
    // Not mutate's per-call callbacks: they are dropped once this unmounts,
    // and a refetch during the request can unmount it.
    withdraw.mutateAsync({ name, iid }).then(onDone, (error: unknown) => {
      onOutcome({ message: panelErrorMessage(error), failed: true });
    });
  };
  return (
    <>
      {!confirming && (
        <button type="button" className="secondary-button" ref={ask} onClick={() => { toggle(true); }}>
          Withdraw {tag}
        </button>
      )}
      {confirming && (
        <div className="def-actions__confirm" role="group" aria-label={`Withdraw ${tag}`}>
          <span>Close {tag} and delete its branch sanctum-def/{name}?</span>
          <button
            type="button"
            ref={confirm}
            className="secondary-button secondary-button--danger"
            disabled={withdraw.isPending}
            onClick={close}
          >
            {withdraw.isPending ? 'Closing…' : `Close ${tag}`}
          </button>
          <button
            type="button"
            className="secondary-button"
            disabled={withdraw.isPending}
            onClick={() => { toggle(false); }}
          >
            Cancel
          </button>
        </div>
      )}
    </>
  );
}

export function DefinitionActions(props: DefinitionActionsProps): ReactElement {
  const { projectId, detail, openUrl, onEdit, closedIid, onClosed, onWithdrawOutcome, refreshing = false } = props;
  const blocked = props.editBlocked ?? null;
  const start = editStart(detail);
  const own = detail.sanctum_mr;
  const done = (result: WithdrawResponse): void => {
    onClosed(result.mr_iid);
    onWithdrawOutcome({ message: withdrawn(result), failed: false });
  };
  return (
    <div className="def-actions">
      {detail.can_edit && start !== null && onEdit !== undefined && (
        <button
          type="button"
          className="secondary-button"
          disabled={refreshing || blocked !== null}
          {...(blocked !== null ? { 'aria-describedby': 'def-edit-blocked' } : {})}
          onClick={() => { onEdit(start); }}
        >
          {own === null ? 'Edit' : `Edit (pushes to !${String(own.iid)})`}
        </button>
      )}
      {detail.can_edit && blocked !== null && (
        <span id="def-edit-blocked" className="def-detail__hint">{blocked}</span>
      )}
      {openUrl !== null && (
        <a href={openUrl} target="_blank" rel="noreferrer" className="secondary-button">
          Open in GitLab
        </a>
      )}
      {detail.can_withdraw && own !== null && own.iid !== closedIid && (
        // Keyed by the MR, so a newer one starts unconfirmed.
        <Withdraw
          key={own.iid}
          projectId={projectId}
          name={detail.summary.name}
          iid={own.iid}
          onDone={done}
          onOutcome={onWithdrawOutcome}
        />
      )}
    </div>
  );
}
